<script setup>
  import { computed, ref, nextTick } from 'vue';

  const props = defineProps({
    tooltipContent: {
      type: String,
      default: '',
    },
    iconClass: {
      type: String,
      default: '',
    },
    row: {
      type: Object,
      default: () => {},
    },
    colorPreset: {
      type: String,
      default: 'green',
      validator: (prop) => ['green', 'red', 'orange', 'info'].includes(prop),
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    isMobile: {
      type: Boolean,
      default: false,
    },
    wrapperClass: {
      type: String,
      default: null,
    },
    link: {
      type: String,
      default: null,
    },
    linkTarget: {
      type: String,
      default: null,
    },
  });

  const emit = defineEmits(['click']);

  //#region data
  const existing = ref(true);
  //#endregion

  //#region computed
  const wrapperProps = computed(() => {
    const result = {};

    if (props.link) {
      result.href = props.link;

      if (props.linkTarget) {
        result.target = props.linkTarget;
      }
    }

    return result;
  });
  //#endregion

  //#region methods
  const handleClick = (event) => {
    // костыль для keep-alive
    existing.value = false;
    nextTick(() => {
      existing.value = true;
    });
    
    emit('click', event);
  };
  //#endregion
</script>

<template>
  <ElTooltip
    v-if="existing"
    class="ui-table-action"
    placement="top"
    :content="tooltipContent"
    popper-class="ui-table-action__popper"
    :show-after="500"
  >
    <component
      :is="link ? 'a' : 'span'"
      class="ui-table-action__icon-wrapper"
      :class="{
        [`ui-table-action__icon-wrapper_color-${colorPreset}`]: colorPreset,
        'ui-table-action__icon-wrapper_disabled': disabled,
        'ui-table-action__icon-wrapper_mobile': isMobile,
        'ui-table-action__icon-wrapper_desktop': !isMobile,
        [wrapperClass]: wrapperClass
      }"
      v-bind="wrapperProps"
      @click="handleClick"
    >
      <i 
        class="ui-table-action__icon" 
        :class="iconClass"
      />
    </component>
  </ElTooltip>
</template>

<style lang="scss">
.ui-table-action {
  &__popper{
    font-size: 11px;
    font-weight: 600;
    line-height: normal;
    border-radius: 8px;
    background-color: rgba(48, 54, 52, 0.9);
    padding: 6px 8px;
  }
  &__icon-wrapper {
    &:hover {
      text-decoration: none;
    }

    &_desktop {
      width: 22px;
      height: 22px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 50%;
      text-decoration: none;
      cursor: pointer;
    }

    &_mobile {
      width: 100%;
      padding: 6px 8px;
      height: 33px;
      border-radius: 0;
      text-align: center;
      font-size: 16px;
      display: flex;
      justify-content: center;
      align-items: center;
    }

    &_color-green {
      color: var(--color-text-green);

      &:hover {
        color: var(--color-text-green);
        background: var(--color-text-green-hover);
      }
    }

    &_color-red {
      color: var(--color-text-red);

      &:hover {
        color: var(--color-text-red);
        background: var(--color-text-red-hover);
      }
    }

    &_color-orange {
      color: var(--color-text-orange);

      &:hover {
        color: var(--color-text-orange);
        background: var(--color-text-orange-hover);
      }
    }

    &_color-info {
      color: var(--color-bg-info);

      &:hover {
        color: var(--color-bg-info);
        background: var(--color-bg-info-hover);
      }
    }

    &_disabled {
      opacity: .5;

      &:hover {
        background: none;
      }

      &:active {
        pointer-events: none;
      }
    }

    @at-root #{selector-append(& + '_disabled', & + '_desktop')} {
      cursor: not-allowed;
      opacity: .35;
    }

    @at-root #{selector-append(& + '_disabled', & + '_color-red')} { 
      opacity: 0.4;
    }

    &_mobile {
      &:hover {
        background: none;
      }
    }
  }
}
</style>